import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";

import TestReportService, {
  TestReportDTO,
  TestReportResultDTO,
  ToleranceRow,
  UpsertRequest,
  UpsertResponse,
} from "../../services/TestReport/TestReport.service";

const parseNum = (val: unknown): number | null => {
  const t = (val ?? "").toString().trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const cleanDecimal = (s: string) =>
  (s ?? "").toString().replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
const normalizeStatus = (s?: string | null) => (s ?? "").trim().toUpperCase() || "—";


const fmtDynamic = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  const s = n.toString();
  if (!s.includes(".")) return s;      
  return s.replace(/\.?0+$/, "");   
};



const CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e6edf3",
  borderRadius: 10,
  padding: "12px",
  marginBottom: 12,
  background: "#f8f9fa",
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
};

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const TITLE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  fontSize: "1.2rem",
  margin: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "rgb(60, 58, 58)",
};

const META_CONTAINER_STYLE: React.CSSProperties = {
  marginTop: 10,
  display: "flex",
  gap: 6,
  alignItems: "center",
  flexWrap: "wrap",
};

const META_BLOCK_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 150,
};

const META_LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  fontWeight: 600,
    fontSize: 13,
  color: "#145326", 
  marginBottom: 4,
};

const META_VALUE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 17,
};

const SAMPLE_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "8px 6px",
  background: "white",
};

const SAMPLE_LABEL_STYLE: React.CSSProperties = { flex: 1, minWidth: 160 };

const INPUT_STYLE: React.CSSProperties = {
  width: 140,
  height: 32,
  padding: "4px 8px",
  textAlign: "right",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: "#f8f9fa",
};

const SCROLLER_STYLE_BASE: React.CSSProperties = {
  overflowY: "auto",
  paddingRight: 8,
  paddingLeft: 2,
};

const DROPDOWN_BUTTON_STYLE: React.CSSProperties = {
  border: "1px solid #233127ff",
  transition: "all 0.2s",
};

interface Props {
  visible: boolean;
  reportId: number | null;
  onHide: () => void;
  onSaved: () => void;
}

type PreviewMap = Record<number, { result: number; status: string | null }>;
type TolMap = Record<number, { min: number | null; max: number | null }>;

const ReportTabsDialog: React.FC<Props> = ({ visible, reportId, onHide, onSaved }) => {
  const toast = useRef<Toast>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<TestReportDTO | null>(null);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewMap>({});
  const [tolerances, setTolerances] = useState<TolMap>({});
  const [autoCalcLoading, setAutoCalcLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!visible || !reportId) return;
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const r = await TestReportService.getReportById(reportId);
        if (!mounted) return;
        setReport(r);

        const seed: Record<string, string> = {};
        (r?.samples ?? []).forEach((sg) =>
          (sg.results ?? []).forEach((res) => {
            if (res.value !== null && res.value !== undefined) {
              seed[`${res.testId}:${sg.sampleId}`] = String(res.value);
            }
          })
        );
        setInputs(seed);

        if (r) {
          const t = await TestReportService.getTolerances(r.productId);
          const map: TolMap = {};
          (t || []).forEach((row: ToleranceRow) => {
            map[row.testId] = { min: row.minTolerance, max: row.maxTolerance };
          });
          if (mounted) setTolerances(map);
        }

        setExpanded(new Set());
        triggerPreview(r?.productId ?? null, seed);
      } catch (err) {
        console.error(err);
        toast.current?.show({ severity: "error", summary: "Failed to load report", life: 2500 });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [visible, reportId]);

  const tests = useMemo(() => {
    if (!report) return [];
    const map: Record<number, { testId: number; testName: string; rows: Array<{ sampleId: number; sampleName: string }>; keys: string[] }> = {};
    for (const sg of report.samples ?? []) {
      for (const res of sg.results ?? []) {
        if (!map[res.testId]) {
          map[res.testId] = { testId: res.testId, testName: res.testName, rows: [], keys: [] };
        }
        map[res.testId].rows.push({ sampleId: sg.sampleId, sampleName: sg.sampleName });
        map[res.testId].keys.push(`${res.testId}:${sg.sampleId}`);
      }
    }
    return Object.values(map).sort((a, b) => a.testName.localeCompare(b.testName));
  }, [report]);

  const completeByTest = useMemo(() => {
    const m: Record<number, boolean> = {};
    for (const t of tests) {
      m[t.testId] = t.keys.every((k) => parseNum(inputs[k]) !== null);
    }
    return m;
  }, [tests, inputs]);

  useEffect(() => {
    if (!report) return;
    triggerPreview(report.productId, inputs);
  }, [inputs, report?.productId]);

  function triggerPreview(productId: number | null, values?: Record<string, string>) {
    if (!productId) return;
    const obj: Record<string, number> = {};
    Object.entries(values ?? inputs).forEach(([k, v]) => {
      const n = parseNum(v);
      if (n !== null) obj[k] = n;
    });

    setAutoCalcLoading(true);
    let cancelled = false;

    (async () => {
      try {
        const list = await TestReportService.calculateResultsPreviewForProduct(productId, obj);
        if (cancelled) return;
        const map: PreviewMap = {};
        (list || []).forEach((r: TestReportResultDTO) => {
          map[r.testId] = { result: r.result, status: r.status };
        });
        setPreview(map);
      } catch (err) {
        console.error("preview error", err);
      } finally {
        if (!cancelled) setAutoCalcLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }

  const getValStr = (testId: number, sampleId: number) => {
    const k = `${testId}:${sampleId}`;
    if (inputs[k] !== undefined) return inputs[k];
    const sg = report?.samples.find((s) => s.sampleId === sampleId);
    const r = sg?.results.find((x) => x.testId === testId);
    return r?.value != null ? String(r.value) : "";
  };

  const setValStr = (testId: number, sampleId: number, raw: string) => {
    const k = `${testId}:${sampleId}`;
    const cleaned = cleanDecimal(raw);
    setInputs((prev) => ({ ...prev, [k]: cleaned }));
  };

  const rangeText = (testId: number) => {
    const tol = tolerances[testId];
    if (!tol) return "Acceptable Range: N.S.";
    const { min, max } = tol;

    if (min != null && max != null) return `Acceptable Range: ${fmtDynamic(min)} – ${fmtDynamic(max)}`;
    if (min != null) return `Acceptable Range: ≥ ${fmtDynamic(min)}`;
    if (max != null) return `Acceptable Range: ≤ ${fmtDynamic(max)}`;
    return "Acceptable Range: N.S.";
  };

  const toggleExpand = (testId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const save = async () => {
    if (!report) return;

    const invalidKeys = Object.entries(inputs).filter(([k, v]) => parseNum(v) === null);
    if (invalidKeys.length > 0) {
      toast.current?.show({ severity: "warn", summary: "Validation Error", detail: "Please enter valid inputs for all samples.", life: 3000 });
      return;
    }

    setSaving(true);
    try {
      const obj: Record<string, number> = {};
      Object.entries(inputs).forEach(([k, v]) => {
        const n = parseNum(v);
        if (n !== null) obj[k] = n;
      });

      const req: UpsertRequest = {
        reportId: report.id,
        productId: report.productId,
        batchNo: report.batchNo,
        sampleQty: report.sampleQty ?? null,
        testedDate: report.testedDate ? new Date(report.testedDate) : null,
        analysisStart: null,
        analysisEnd: null,
        inputs: obj,
      };
      const res: UpsertResponse = await TestReportService.upsertReportWithResults(req);

      if (res?.report) {
        setReport(res.report);
        const refreshed: Record<string, string> = {};
        res.report.samples?.forEach((sg) =>
          sg.results.forEach((r) => {
            if (r.value !== null && r.value !== undefined) {
              refreshed[`${r.testId}:${sg.sampleId}`] = String(r.value);
            }
          })
        );
        setInputs((prev) => ({ ...refreshed, ...prev }));
      }

      if (res?.results) {
        const map: PreviewMap = {};
        res.results.forEach((r) => (map[r.testId] = { result: r.result, status: r.status }));
        setPreview(map);
      }

      toast.current?.show({ severity: "success", summary: "Saved", life: 1800 });
      onSaved();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Save failed", life: 2400 });
    } finally {
      setSaving(false);
    }
  };

  const scrollerStyle: React.CSSProperties = {
    ...SCROLLER_STYLE_BASE,
    maxHeight: isMaximized ? "80vh" : "56vh",
  };

  return (
    <Dialog
      header={report ? `${report.productName} — Batch ${report.batchNo}` : "Loading…"}
      visible={visible}
      onHide={() => { setIsMaximized(false); onHide(); }}
      closable
      maximizable
      onMaximize={() => setIsMaximized((s) => !s)}
      modal
      style={{ width: "96vw", maxWidth: 1200 }}
      contentStyle={{ padding: 16 }}
    >
      <Toast ref={toast} />
      {loading || !report ? (
        <div className="p-5">Loading…</div>
      ) : (
        <div>
          <div style={scrollerStyle}>
            {tests.map((t) => {
              const isComplete = completeByTest[t.testId];
              const pv = preview[t.testId];
              const hideStatus =
                !!report?.productName?.toUpperCase().includes("ALMOND") &&
                t.testName.trim().toUpperCase() === "UNSAPONIFICATION VALUE";
              const isOpen = expanded.has(t.testId);

              return (
                <div key={t.testId} style={CARD_STYLE}>
                  <div style={HEADER_STYLE}>
                    <h3 style={TITLE_STYLE} title={t.testName}>{t.testName}</h3>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Button
                        icon={isOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                        onClick={() => toggleExpand(t.testId)}
                        className="p-button-text"
                        style={DROPDOWN_BUTTON_STYLE}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = "#0a9130ff")}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = "#233127ff")}
                      />
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #eef2f6', ...META_CONTAINER_STYLE }}>
                    <div style={META_BLOCK_STYLE}>
                      <div style={META_LABEL_STYLE}>Result</div>
                      <div style={META_VALUE_STYLE}>
                        {isComplete && pv ? fmtDynamic(Number(pv.result)) : isComplete && autoCalcLoading ? "…" : "—"}
                      </div>
                    </div>

                    <div style={META_BLOCK_STYLE}>
                      <div style={META_LABEL_STYLE}>Status</div>
                      <div>
                        {hideStatus ? (
                          <span style={{ color: "transparent" }}>.</span>
                        ) : (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "6px 10px",
                              borderRadius: 6,
                              color: "#fff",
                              background:
                                isComplete && pv
                                  ? pv.status === "PASS"
                                    ? "#16a34a"
                                    : pv.status === "FAIL"
                                    ? "#dc2626"
                                    : "#6b7280"
                                  : "#6b7280",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            {isComplete && pv ? normalizeStatus(pv.status) : "—"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ marginLeft: "auto", minWidth: 220 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#145326" }}>{rangeText(t.testId)}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                      {(t.rows || []).map((r, idx) => {
                        const key = `${t.testId}:${r.sampleId}`;
                        const showBorder = (t.rows?.length ?? 0) > 1 && idx < (t.rows?.length ?? 0) - 1;
                        const rowStyle = {
                          ...SAMPLE_ROW_STYLE,
                          borderBottom: showBorder ? "1px solid #c2b9b9ff" : "none",
                        };
                        return (
                          <div key={key} style={rowStyle}>
                            <div style={SAMPLE_LABEL_STYLE}>{r.sampleName}</div>
                            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                              <InputText
                                value={getValStr(t.testId, r.sampleId)}
                                onInput={(e) => {
                                  const raw = (e.target as HTMLInputElement).value;
                                  const cleaned = cleanDecimal(raw);
                                  if (cleaned !== raw) (e.target as HTMLInputElement).value = cleaned;
                                  setValStr(t.testId, r.sampleId, cleaned);
                                }}
                                style={INPUT_STYLE}
                                inputMode="decimal"
                                placeholder="0"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Button label="Cancel" severity="secondary" onClick={() => { setIsMaximized(false); onHide(); }} />
            <Button label="Save" icon="pi pi-save" loading={saving} onClick={save} />
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ReportTabsDialog;
